import getConfig from '~/config';

// Mapbox GL's RequestParameters.credentials only allows 'same-origin' | 'include'.
type TransformedRequest = { url: string; credentials?: 'same-origin' | 'include' };

/** True when `url`'s origin matches one of `bases` (our own tile/api servers). */
export function isCredentialedOrigin(url: string, bases: string[]): boolean {
  let origin: string;
  try {
    origin = new URL(url).origin;
  } catch {
    // Relative/asset URLs (e.g. Mapbox sprite paths) — never credentialed.
    return false;
  }
  return bases.some((base) => {
    try {
      return new URL(base).origin === origin;
    } catch {
      return false;
    }
  });
}

/**
 * Builds a Mapbox GL `transformRequest`. Requests to one of `bases` get `credentials: 'include'`
 * so the browser sends the HttpOnly auth cookie; everything else (Mapbox sprites/glyphs/styles,
 * third-party tiles) is left untouched. Exported for unit testing without config.
 */
export function makeMapAuthTransformRequest(bases: string[]) {
  return (url: string): TransformedRequest =>
    isCredentialedOrigin(url, bases) ? { url, credentials: 'include' } : { url };
}

const config = getConfig();

/**
 * Sends the HttpOnly auth cookie on map requests to our tile/api servers so tiles authenticate in
 * cookie mode. In bearer mode auth still travels via the `?token=` param (see `addToken`); here
 * `credentials: 'include'` is a harmless no-op. Other origins are untouched so Mapbox assets work.
 * TODO(INFRA-218): the sole tile-auth channel once rolled out — then `addToken` can drop the param.
 */
export const mapAuthTransformRequest = makeMapAuthTransformRequest([config.tileServerBase, config.apiServerBase]);
